import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import api from '../../api';
import ActionButton from '../../components/ActionButton';
import DraggableModal from '../../components/DraggableModal';
import ErrorAlert from '../../components/ErrorAlert';
import {
  MultiPicker,
  RETRIEVAL_LANE_OPTIONS,
  SelectField,
  TextArea,
  TextInput,
} from './PlaceWorkbenchParts';

const ADD_PLACE_DEFAULTS = {
  place_name: '',
  retrieval_lane: 'recommendation',
  area: '',
  town_or_community: '',
  sector_primary: '',
  category_type: '',
  category: '',
  website_url: '',
  source_url: '',
  address: '',
  phone: '',
  email: '',
  guest_use_case_tags: [],
  pitch: '',
  utility_kind: '',
  practical_note: '',
  admin_notes: '',
};

function activeApprovedTags(tags) {
  return (tags || [])
    .filter(tag => tag.active !== false && tag.vocabulary_status !== 'pending')
    .map(tag => tag.name)
    .filter(Boolean);
}

function AddPlaceModal({ tags, vocab, onClose, onCreated }) {
  const [form, setForm] = useState(ADD_PLACE_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const submit = async () => {
    if (!form.place_name.trim()) {
      setError('Place name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await api.createPlaceCard(form);
      await onCreated(created);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title="Add Place" initialWidth={720} initialHeight={Math.round(window.innerHeight * 0.82)}>
      <div className="h-full space-y-4 overflow-y-auto p-5">
        {error && <ErrorAlert error={error} onClose={() => setError('')} />}
        <TextInput label="Place name" value={form.place_name} onChange={value => update('place_name', value)} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SelectField
            label="Retrieval lane"
            value={form.retrieval_lane}
            options={RETRIEVAL_LANE_OPTIONS}
            onChange={value => update('retrieval_lane', value || 'recommendation')}
          />
          <TextInput label="Area" value={form.area} onChange={value => update('area', value)} />
        </div>
        <TextInput label="Town / community" value={form.town_or_community} onChange={value => update('town_or_community', value)} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput label="Website" value={form.website_url} onChange={value => update('website_url', value)} />
          <TextInput label="Source" value={form.source_url} onChange={value => update('source_url', value)} />
        </div>
        <TextInput label="Address" value={form.address} onChange={value => update('address', value)} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput label="Phone" value={form.phone} onChange={value => update('phone', value)} />
          <TextInput label="Email" value={form.email} onChange={value => update('email', value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SelectField label="Place sector" value={form.sector_primary} options={vocab.sector || []} onChange={value => update('sector_primary', value)} />
          <SelectField label="Place category" value={form.category_type} options={vocab.category_type || []} onChange={value => update('category_type', value)} />
          <SelectField label="Initial card category" value={form.category} options={vocab.category || []} onChange={value => update('category', value)} />
        </div>
        <MultiPicker
          label="Card tags"
          selected={form.guest_use_case_tags}
          options={activeApprovedTags(tags)}
          onChange={value => update('guest_use_case_tags', value)}
          accent
        />
        {form.retrieval_lane === 'practical_find' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SelectField label="Utility kind" value={form.utility_kind} options={vocab.utility_kind || []} onChange={value => update('utility_kind', value)} />
            <TextArea label="Practical note" value={form.practical_note} onChange={value => update('practical_note', value)} rows={2} />
          </div>
        )}
        <TextArea label="Pitch" value={form.pitch} onChange={value => update('pitch', value)} rows={3} />
        <TextArea label="Admin notes" value={form.admin_notes} onChange={value => update('admin_notes', value)} rows={3} placeholder="Internal notes..." />
        <div className="flex gap-3 border-t border-stone-100 pt-3">
          <ActionButton variant="primary" onClick={submit} loading={saving}>Create place</ActionButton>
          <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
        </div>
      </div>
    </DraggableModal>
  );
}

function FileInput({ files, setFiles, label, fileKey }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="file"
          accept=".csv"
          onChange={event => setFiles(current => ({ ...current, [fileKey]: event.target.files[0] || null }))}
          className="text-sm text-stone-600 file:mr-3 file:rounded-lg file:border file:border-stone-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:text-stone-700 hover:file:bg-stone-50"
        />
        {files[fileKey] && (
          <button
            type="button"
            onClick={() => setFiles(current => ({ ...current, [fileKey]: null }))}
            className="text-stone-400 hover:text-stone-600"
            aria-label={`Clear ${label}`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {files[fileKey] && <p className="mt-1 text-xs text-stone-500">{files[fileKey].name}</p>}
    </div>
  );
}

function ImportPlacesModal({ onClose, onImported }) {
  const [files, setFiles] = useState({ master: null, admin: null, signals: null });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!files.master && !files.admin && !files.signals) {
      setError('Select at least one CSV file');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      if (files.master) fd.append('master_csv', files.master);
      if (files.admin) fd.append('admin_csv', files.admin);
      if (files.signals) fd.append('signals_csv', files.signals);
      const importResult = await api.importPlaces(fd);
      setResult(importResult);
      await onImported(importResult);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title="Upload CSVs" initialWidth={540} initialHeight={460}>
      <div className="h-full space-y-5 overflow-y-auto p-6">
        {error && <ErrorAlert error={error} onClose={() => setError('')} />}
        {result ? (
          <div className="space-y-4">
            <div className="space-y-1 rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-medium text-green-800">Import successful</p>
              <p className="text-xs text-green-700">Places upserted: {result.places_upserted}</p>
              <p className="text-xs text-green-700">Admin cards upserted: {result.admin_cards_upserted}</p>
              {result.admin_cards_from_queue !== undefined && <p className="text-xs text-green-700">From admin queue: {result.admin_cards_from_queue}</p>}
              {result.admin_cards_derived_from_master !== undefined && <p className="text-xs text-green-700">Derived from master: {result.admin_cards_derived_from_master}</p>}
              {result.working_cards_upserted !== undefined && <p className="text-xs text-green-700">Working cards upserted: {result.working_cards_upserted}</p>}
              <p className="text-xs text-green-700">Signals reloaded: {result.signals_reloaded}</p>
            </div>
            <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-600">Upload any combination. Master can be skipped when the latest master is already imported.</p>
            <FileInput files={files} setFiles={setFiles} label="Master CSV" fileKey="master" />
            <FileInput files={files} setFiles={setFiles} label="Admin queue CSV" fileKey="admin" />
            <FileInput files={files} setFiles={setFiles} label="Signal proposals CSV" fileKey="signals" />
            <div className="flex gap-3 border-t border-stone-100 pt-2">
              <ActionButton variant="primary" onClick={run} loading={loading} icon={Upload}>
                {loading ? 'Uploading...' : 'Upload'}
              </ActionButton>
              <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
            </div>
          </>
        )}
      </div>
    </DraggableModal>
  );
}

export { AddPlaceModal, ImportPlacesModal };
